import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Toast, ToastProps } from './Toast';

interface ToastContextType {
  showToast: (
    message: string,
    severity?: ToastProps['severity'],
    options?: Partial<ToastProps>
  ) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toastState, setToastState] = useState<{
    open: boolean;
    message: string;
    severity: ToastProps['severity'];
    autoHideDuration?: number;
    anchorOrigin?: ToastProps['anchorOrigin'];
    variant?: ToastProps['variant'];
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const showToast = (
    message: string,
    severity: ToastProps['severity'] = 'info',
    options: Partial<ToastProps> = {}
  ) => {
    setToastState({
      open: true,
      message,
      severity,
      ...options,
    });
  };

  const hideToast = () => {
    setToastState((prev) => ({ ...prev, open: false }));
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Toast
        open={toastState.open}
        message={toastState.message}
        severity={toastState.severity}
        autoHideDuration={toastState.autoHideDuration}
        onClose={hideToast}
        anchorOrigin={toastState.anchorOrigin}
        variant={toastState.variant}
      />
    </ToastContext.Provider>
  );
};
